import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useContractContext, usePostMessageContext, useTheme, useStrings } from '@providers';
import { FaqButton } from '@components/ContractDetails/StyledContractDetails';
import AddAndDeclineProtection, { StyledAddAndDeclineProtection } from '@components/Shared/AddAndDeclineProtection';
import Dropdown from '@components/Dropdown';
import { StyledDropdown } from '@components/Dropdown/StyledDropdown';
import DropdownContractLabel from '@components/Dropdown/DropdownContractLabel';
import { ReactComponent as Bullet } from '@images/bullet-checkmark.svg';
import { ReactComponent as FAQIcon } from '@images/faq-icon.svg';

import { IMainProps } from './Main/index';
import TermsLink from './Main/TermsLink';

const DetailsList = styled.ul`
  list-style: none;
  margin: 0 0 32px;
  padding: 0;

  ${FaqButton} {
    display: flex;
    align-items: center;
  }
`;

const Detail = styled.li`
  min-height: 1.5em;
  font-weight: 400;
  font-size: 13px;
  line-height: 16.25px;
  margin-bottom: 12px;
  position: relative;

  @media (min-width: 551px) {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 8px;
  }

  span {
    &.icon-container {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 0;
      left: 0;
    }
    &.bullet-text {
      display: block;
      margin-left: 27px;
    }
  }

  &:last-child {
    margin: 0;
  }
`;

const Checkmark = styled(Bullet)`
  width: 12px;
  height: 9px;
  color: ${({ theme }) => theme.merchantColor};
  fill: ${({ theme }) => theme.merchantColor};
`;

const Point = ({ children }: { children: React.ReactNode }) => (
  <Detail>
    <span className="icon-container">
      <Checkmark />
    </span>
    <span className="bullet-text">{children}</span>
  </Detail>
);

const StyledReducedDecision = styled.div`
  position: relative;

  @media (max-width: 550px) and (min-height: 660px) {
    position: static;
    min-height: auto;
    padding-bottom: 0;
  }

  h1 {
    font-weight: 500;
    font-size: 24px;
    text-align: left;

    @media (min-width: 551px) {
      font-size: 36px;
    }
  }

  ${StyledDropdown} {
    padding: 0;
    position: relative;

    // TODO: clean this up when we refactor later
    .dropdown__menu__container {
      position: absolute;
      left: 0;
      right: 0;
      top: 100%;
      width: auto !important;
      margin: 7px 0 0 0;

      ol {
        box-shadow: 0px 3px 12px rgba(0, 0, 0, 0.12);
      }
    }
  }

  ${StyledAddAndDeclineProtection} {
    margin-top: 0;

    .plandata-tab__add-button {
      width: 100%;
    }

    @media (min-width: 551px) {
      font-size: 14px;
    }
  }
`;

function ReducedDecision({ onFAQClicked, closeButtonRef }: IMainProps) {
  const { contracts, selectContract, selectedContract, selectedContractId } = useContractContext();
  const { merchantColor } = useTheme();
  const { postMessage } = usePostMessageContext();
  const { bullets, headline } = useStrings();

  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  // Focus the close button by default
  React.useEffect(() => {
    closeButtonRef?.current?.focus();
  }, [closeButtonRef]);

  React.useEffect(() => {
    if (dropdownOpen) {
      postMessage('dropdownMenuOpened', { ctaType: 'modal' });
    }
  }, [dropdownOpen]);

  const options = useMemo(() => {
    return contracts.map(contract => ({
      value: contract.id,
      label: <DropdownContractLabel contract={contract} />,
    }));
  }, [contracts]);

  if (!selectedContract || !selectedContractId) {
    return null;
  }

  return (
    <StyledReducedDecision>
      <h1>{headline}</h1>
      <DetailsList>
        {bullets.map((bullet: string, index: number) => <Point key={index}>{bullet}</Point>)}
        <Detail>
          <FaqButton onClick={onFAQClicked} type="button">
            <span className="icon-container">
              <FAQIcon />
            </span>
            <span className="bullet-text">
              <span className="text">FAQ</span>
            </span>
          </FaqButton>
        </Detail>
      </DetailsList>

      <Dropdown
        color={merchantColor}
        isOpen={dropdownOpen}
        onToggle={setDropdownOpen}
        value={selectedContractId}
        onChange={selectContract}
        options={options}
      />

      <AddAndDeclineProtection />
      <TermsLink position="under" />
    </StyledReducedDecision>
  );
}

export default ReducedDecision;
